<script lang="ts">
	import { getContext, onMount } from "svelte";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import type { Poll } from "#gen/forms/v1/poll_pb.js";
	import type { Page } from "#gen/pages/v2/page_pb.js";
	import Label from "#lib/components/Label.svelte";
	import Toggle from "#lib/components/Toggle.svelte";
	import PlaintextEditor from "#lib/components/content-editors/PlaintextEditor.svelte";
	import AppearanceItem from "#lib/components/flow/editor/appearance/AppearanceItem.svelte";
	import ButtonAppearance from "#lib/components/page/components/poll/appearance/ButtonAppearance.svelte";
	import { DEFAULT_RESTART_BUTTON_LABEL } from "#lib/components/page/components/poll/constants.js";
	import { PollState } from "#lib/components/page/components/poll/stateMachine.js";
	import { getDefaults, getTheme, setThemeValue } from "#lib/components/page/components/poll/theme.js";
	import { setEditorState } from "#lib/components/page/components/poll/utils.js";
	import { setStructValue, structValue, unsetValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { JsonObject, JsonValue } from "@bufbuild/protobuf";
	import type { Writable } from "svelte/store";

	interface Props {
		id: string;
		page: Writable<Page>;
		poll: Poll;
		onInput?: (poll: Poll) => void;
		onChange?: (poll: Poll) => void;
	}

	const editorState: Writable<Record<string, unknown>> = getContext("editorState");
	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");

	let { poll, id, page, onInput, onChange }: Props = $props();

	const config = $derived(getConfig($page, id));
	const themeDefaults = $derived(getDefaults($brandKit, $page));
	const theme = $derived(getTheme($brandKit, $page, config));

	onMount(() => {
		$editorState = setEditorState($editorState, poll, "overridePollState", PollState.Closed);
		$editorState = setEditorState($editorState, poll, "overrideSelection", true);
	});

	function getConfig(page: Page, id: string): JsonObject {
		let config = structValue(page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue(page.config, id, config);
		}
		return config;
	}

	function handleToggleChange(e: Event, key: string) {
		const target = e.target as HTMLInputElement;
		setThemeValue($brandKit, $page, config, key, target.checked);
		onChange?.(poll);
	}

	function handleChange(key: string, value: JsonValue) {
		setThemeValue($brandKit, $page, config, key, value);
		onChange?.(poll);
	}

	function handleInput(key: string, value: JsonValue) {
		setThemeValue($brandKit, $page, config, key, value);
		onInput?.(poll);
	}

	function resetToPageStyle(key: string) {
		const config = structValue($page.config, id);
		if (config) {
			unsetValue(config, key);
			onChange?.(poll);
		}
	}
</script>

<div class="flex w-full flex-col gap-6">
	<AppearanceItem title="Poll cards">
		{#snippet content()}
			<Toggle checked={(theme["showPollTitlesInResults"] as boolean) ?? themeDefaults["showPollTitlesInResults"]} on:change={e => handleToggleChange(e, "showPollTitlesInResults")}>Show poll titles</Toggle>
			<Toggle checked={(theme["showPollBorderInResults"] as boolean) ?? themeDefaults["showPollBorderInResults"]} on:change={e => handleToggleChange(e, "showPollBorderInResults")}>Show poll border</Toggle>
			<Toggle checked={(theme["showPollBackgroundColorInResults"] as boolean) ?? themeDefaults["showPollBackgroundColorInResults"]} on:change={e => handleToggleChange(e, "showPollBackgroundColorInResults")}>Show poll background color</Toggle>
		{/snippet}
	</AppearanceItem>
	<AppearanceItem title="Restart button">
		{#snippet content()}
			<Label for="restart-button-text" class="font-medium">Text value</Label>
			<PlaintextEditor id="restart-button-text" provider={$tokenProvider} bind:value={poll.restartButtonText} placeholder={DEFAULT_RESTART_BUTTON_LABEL} on:input={() => onInput?.(poll)} on:change={() => onChange?.(poll)} />
		{/snippet}
		<ButtonAppearance prefix="restart" defaults={themeDefaults} values={theme} onInput={handleInput} onChange={handleChange} onReset={resetToPageStyle} />
	</AppearanceItem>
</div>
